import React from "react";
import { Box, Container, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SecurityIcon from "@mui/icons-material/Security";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useTheme } from "@emotion/react";

const features = [
    {
        title: "Banking",
        desc: "Apply for credit cards from top banks, compare benefits quickly, and manage everything securely with paperless support.",
        icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />,
        cardBg: "#EEF2FF",
        iconColor: "#4F46E5",
    },
    {
        title: "Investments",
        desc: "Start SIPs or lump-sum investments in mutual funds with goal-based guidance and smart tools for better growth.",
        icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
        cardBg: "#E0F2FE",
        iconColor: "#0284C7",
    },
    {
        title: "Insurance",
        desc: "Explore investment-linked insurance plans, service benefits, and smart banking solutions curated for clarity and convenience.",
        icon: <SecurityIcon sx={{ fontSize: 18 }} />,
        cardBg: "#ECFDF5",
        iconColor: "#059669",
    },
    {
        title: "Service Benefits",
        desc: "Get in-app support, FAQs, financial guides, and updates to make your financial life simpler, faster, and stress-free.",
        icon: <DonutLargeIcon sx={{ fontSize: 18 }} />,
        cardBg: "#FCE7F3",
        iconColor: "#DB2777",
    },
];


const Features = () => {

    const theme = useTheme();

    return (
        <Box sx={{ py: 4, position: "relative", overflow: "hidden" }}>
            <Container maxWidth="lg">


                <Box
                    sx={{
                        mb: { xs: 3, md: 4 },
                        textAlign: "center",
                    }}
                >

                    <Typography
                        sx={{
                            fontSize: "13px",
                            letterSpacing: "2px",
                            fontWeight: 600,
                            mb: 1,
                            color: theme.palette.primary.main,
                        }}
                        data-aos="fade-right"
                    >
                        • CORE SERVICES
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: "20px", sm: "28px", md: "40px" },
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            position: "relative",
                            display: "inline-block",
                        }}
                    >
                        More{" "}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 900,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                position: "relative",
                                display: "inline-block",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: "-4px",
                                    left: 0,
                                    width: "100%",
                                    height: "2px",
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                                },
                            }}
                        >
                            Additional
                        </Box>{" "}
                        Features
                    </Typography>
                </Box>






                {/* CARDS */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                            md: "1fr 1fr",
                        },
                        gap: 4,
                        mb: 10,
                        width: "100%",
                    }}
                >
                    {features.map((item, index) => (
                        <FeatureCard key={index} item={item} />
                    ))}
                </Box>



                <Box
                    sx={{
                        position: "absolute",
                        top: "75%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "100%",
                        display: { xs: "none", md: "flex" }, justifyContent: "center",
                        gap: 8,
                        zIndex: 0,
                        pointerEvents: "none",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: 40, md: 100 },
                            fontWeight: 700,

                            color: "#e4e8f4",
                            letterSpacing: 18,
                            transform: "translateX(-70px)",
                        }}
                    >
                        Arth
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: 40, md: 100 },
                            fontWeight: 700,
                            color: "#e4e8f4",
                            letterSpacing: 10,
                            transform: "translateX(100px)",
                        }}
                    >
                        Gyan
                    </Typography>
                </Box>

                {/* PHONE IMAGE */}
                <Box
                    data-aos="zoom-in"
                    data-aos-duration="1000"
                    data-aos-easing="ease-out-cubic"
                    sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        zIndex: 2,
                        mt: -6,
                    }}
                >

                    <Box
                        component="img"
                        src="https://ik.imagekit.io/arthgyan/Arthgyan_Web/websi-removebg-preview.png"
                        alt="Mobile UI"
                        sx={{
                            width: { xs: 360, md: 480 },
                            display: "block",
                        }}
                    />

                    {/* BOTTOM FADE EFFECT */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "100%",
                            height: "25%",
                            background: `linear-gradient(
  to bottom,
  ${theme.palette.primary.main}00 0%,
  ${theme.palette.primary.main}22 60%,
  ${theme.palette.primary.main}33 100%
)`


                        }}
                    />
                </Box>
            </Container>
        </Box>
    );
};

const FeatureCard = ({ item }) => {
    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "flex-start",
                gap: 3,
                padding: "20px 24px",
                borderRadius: "14px",
                background: item.cardBg,
                position: "relative",
                cursor: "pointer",
                transition: "all 0.35s ease",

                "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: `0 10px 25px ${item.iconColor}40`,
                },

                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "14px",
                    padding: "1.5px",
                    background: `linear-gradient(135deg, ${item.iconColor}, transparent 70%)`,
                    WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    opacity: 0,
                    transition: "opacity 0.35s ease",
                },

                "&:hover::before": {
                    opacity: 1,
                },
            }}
        >
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    background: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: item.iconColor,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                    "&:hover": {
                        transform: "rotate(8deg) scale(1.1)",
                    },
                }}
            >
                {item.icon}
            </Box>

            <Box>
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#111827" }}>
                    {item.title}
                </Typography>

                <Typography sx={{ fontSize: "13px", color: "#6B7280", mt: 0.5, lineHeight: 1.5 }}>
                    {item.desc}
                </Typography>
            </Box>
        </Box>
    );
};


export default Features;